// src/app/contexts/auth/Provider.jsx
import { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n/config';
import { AuthContext } from './context';
import {
  initKeycloak,
  doLogin,
  doLogout,
  getUserProfile,
  syncUser,
  refreshToken,
} from 'utils/keycloak';

/**
 * Check if the current route is a public route that doesn't require authentication
 */
const isPublicRoute = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname.startsWith('/website');
  }
  return false;
};

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  errorMessage: null,
  user: null,
  tenantNotAssigned: false,
  accessDeniedReason: null,
};

const reducerHandlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, tenantNotAssigned, accessDeniedReason } =
      action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      tenantNotAssigned: tenantNotAssigned || false,
      accessDeniedReason: accessDeniedReason || null,
      user,
    };
  },

  LOGIN_REQUEST: (state) => {
    return {
      ...state,
      isLoading: true,
    };
  },

  LOGIN_SUCCESS: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      user,
    };
  },

  LOGIN_ERROR: (state, action) => {
    const { errorMessage } = action.payload;
    return {
      ...state,
      errorMessage,
      isLoading: false,
    };
  },

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),

  UPDATE_USER: (state, action) => ({
    ...state,
    user: {
      ...state.user,
      ...action.payload,
    },
  }),
};

const reducer = (state, action) => {
  const handler = reducerHandlers[action.type];
  if (handler) {
    return handler(state, action);
  }
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // For public routes, skip authentication and mark as initialized
    if (isPublicRoute()) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      return;
    }

    const handleAuthSuccess = async () => {
      let user;

      // Try to sync user with backend (creates/updates user in database)
      try {
        user = await syncUser();
        // Set picture: prefer backend avatarUrl, fallback to Keycloak picture
        if (user.avatarUrl) {
          user.picture = user.avatarUrl;
        } else {
          const keycloakProfile = getUserProfile();
          user.picture = keycloakProfile?.picture;
        }

        // Apply user's language preference
        if (user.language && ['en', 'fr'].includes(user.language)) {
          i18n.changeLanguage(user.language);
        }
      } catch (error) {
        // Backend rejected the user even though Keycloak authenticated them.
        // Surface the reason so the SPA can show a clear blocking screen
        // instead of dumping them into the app.
        //   1010 EX_TENANT_NOT_ASSIGNED — non-admin without tenant claim
        //   1012 EX_NO_ROLES_ASSIGNED   — Keycloak account has no roles
        //   1014 EX_ACCOUNT_SUSPENDED   — account suspended by an admin
        //   1015 EX_TENANT_DISABLED     — tenant deactivated
        //   1016 EX_TENANT_EXPIRED      — tenant contract end_date passed
        const BLOCKING_CODES = {
          1010: 'no-tenant',
          1012: 'no-roles',
          1014: 'suspended',
          1015: 'tenant-disabled',
          1016: 'tenant-expired',
        };

        if (error?.code && BLOCKING_CODES[error.code]) {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: null,
              tenantNotAssigned: error.code === 1010,
              accessDeniedReason: BLOCKING_CODES[error.code],
            },
          });
          return;
        }

        console.warn(
          'User sync with backend failed, using Keycloak profile:',
          error.message
        );
        // Fallback to Keycloak profile if sync fails
        user = getUserProfile();
      }

      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: true,
          user,
        },
      });

      // Set up token refresh
      const tokenRefreshInterval = setInterval(() => {
        refreshToken().catch(() => {
          // If token refresh fails, log out
          handleLogout();
        });
      }, 60000); // Check every minute

      return () => clearInterval(tokenRefreshInterval);
    };

    const handleAuthError = (error) => {
      console.error('Auth initialization error:', error);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    };

    // Initialize Keycloak
    initKeycloak(handleAuthSuccess, handleAuthError);
  }, []);

  const login = () => {
    dispatch({ type: 'LOGIN_REQUEST' });

    try {
      doLogin();
      // Note: The actual login success will be handled by the Keycloak callback
    } catch (err) {
      dispatch({
        type: 'LOGIN_ERROR',
        payload: {
          errorMessage: err.message || 'Failed to login',
        },
      });
    }
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const logout = () => {
    handleLogout();
    doLogout();
  };

  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  // This is the key fix - AuthContext is likely not a component but an object with Provider/Consumer
  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
